import CancelTicket from '../../components/CancelTicket/CancelTicket';
import PageLoader from '../../components/PageLoader/PageLoader';
import { TableContext } from '../../contexts/tableContext';
import { getCookies } from '../../hooks/useCookies';
import { SERVER_BASE_URL } from '../../redux/apiConstants';
import { DateFormatter } from '../../utilityFunctions/utilsFunctions';
import styles from './viewTicket.module.scss';
import axios from 'axios';
import { useCallback, useEffect, useState, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

// ─── helpers ────────────────────────────────────────────────────────────────

const fmt = (amount) => {
  if (amount == null) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const fmtDate = (d) => {
  if (!d) return '—';
  try {
    return DateFormatter(new Date(d).toISOString(), 'toEventViewDate');
  } catch {
    return '—';
  }
};

const fmtDateTime = (d) => {
  if (!d) return '—';
  try {
    const iso = new Date(d).toISOString();
    return `${DateFormatter(iso, 'toEventViewDate')} at ${DateFormatter(iso, 'toTime')}`;
  } catch {
    return '—';
  }
};

const getInitials = (name) => {
  if (!name) return 'U';
  const parts = name.trim().split(' ');
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : name[0]?.toUpperCase() || 'U';
};

const isFileUrl = (r) => r && (r.startsWith('http://') || r.startsWith('https://'));

const getFileName = (url) => {
  if (!url) return 'View File';
  try {
    return url.split('/').pop().replace(/^\d+-/, '') || 'View File';
  } catch {
    return 'View File';
  }
};

const getTimeAgo = (d) => {
  if (!d) return '';
  try {
    const diffMins = Math.floor((Date.now() - new Date(d)) / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hr${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  } catch {
    return '';
  }
};

// ─── inline style tokens ─────────────────────────────────────────────────────
const card = {
  background: '#fff',
  borderRadius: '12px',
  border: '1px solid #E5E7EB',
  padding: '24px',
  marginBottom: '20px',
  width: '100%',
  boxSizing: 'border-box',
};
const cardTitle = {
  fontSize: '15px',
  fontWeight: '600',
  color: '#111827',
  margin: '0 0 18px',
};
const label = { fontSize: '12px', color: '#9CA3AF', marginBottom: '2px' };
const value = { fontSize: '14px', color: '#111827', fontWeight: '500' };
const infoRow = { display: 'flex', flexDirection: 'column', marginBottom: '14px' };
const badgePaid = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  background: '#DCFCE7',
  color: '#166534',
  borderRadius: '20px',
  padding: '4px 12px',
  fontSize: '13px',
  fontWeight: '600',
};
const badgeCancelled = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  background: '#FEE2E2',
  color: '#991B1B',
  borderRadius: '20px',
  padding: '4px 12px',
  fontSize: '13px',
  fontWeight: '600',
};
const badgePending = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  background: '#FEF9C3',
  color: '#854D0E',
  borderRadius: '20px',
  padding: '4px 12px',
  fontSize: '13px',
  fontWeight: '600',
};
const badgeWaitlisted = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  background: '#EDE9FE',
  color: '#5B21B6',
  borderRadius: '20px',
  padding: '4px 12px',
  fontSize: '13px',
  fontWeight: '600',
};
const breakdownRow = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '10px 0',
  borderBottom: '1px solid #F3F4F6',
};
const breakdownTotal = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '14px 0 0',
  fontWeight: '700',
  fontSize: '15px',
};
const formResponseItem = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  padding: '12px 0',
  borderBottom: '1px solid #F3F4F6',
};
const btnPrimary = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 18px',
  borderRadius: '8px',
  border: 'none',
  background: '#39979d',
  color: '#fff',
  fontWeight: '600',
  fontSize: '14px',
  cursor: 'pointer',
  width: '100%',
  justifyContent: 'center',
  marginBottom: '10px',
};
const btnDanger = {
  ...btnPrimary,
  background: '#DC2626',
};

// ─── component ───────────────────────────────────────────────────────────────
function ViewTicket() {
  const { dashboardStyle, setDashboardHeader, bodyStyle } = useContext(TableContext);
  const navigate = useNavigate();
  const { ticketId } = useParams();

  const token = `Bearer ${getCookies('Token')}`;
  const [loading, setLoading] = useState(true);
  const [orderDetails, setOrderDetails] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [ticketConfirmModal, setTicketConfirmModal] = useState({
    open: false,
    type: null,
    willWaitlist: false,
  });
  const [ticketApprovalBusy, setTicketApprovalBusy] = useState(false);
  const [approvalPreviewLoading, setApprovalPreviewLoading] = useState(false);

  const fetchDetails = useCallback(async () => {
    if (!ticketId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const res = await axios.get(`${SERVER_BASE_URL}/orders/${ticketId}/view`, {
        headers: { Authorization: token },
      });
      if (res.status === 200 && res.data.code === 200) {
        setOrderDetails(res.data.data?.orderDetails || null);
      } else {
        toast.error(res.data?.message || 'Failed to load ticket details');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to load ticket details');
    } finally {
      setLoading(false);
    }
  }, [ticketId, token]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setDashboardHeader('Ticket Details');
    fetchDetails();
  }, []);

  const confirmDelete = async () => {
    setDeleteLoading(true);
    try {
      const response = await axios.post(
        `${SERVER_BASE_URL}/booking/cancel-booking/${ticketId}`,
        { cancelledBy: getCookies('USERID') },
        { headers: { Authorization: token } }
      );
      if (response.data.code === 200) {
        toast.success('Ticket has been cancelled.');
        setDeleteModal(false);
        fetchDetails();
      } else {
        toast.error(response.data?.message || 'Something went wrong.');
        setDeleteModal(false);
      }
    } catch {
      toast.error('Something went wrong.');
      setDeleteModal(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleOpenTicketConfirmReject = () => {
    setTicketConfirmModal({ open: true, type: 'reject', willWaitlist: false });
  };

  const handlePrepareApproveConfirm = async () => {
    if (!ticketId) return;
    setApprovalPreviewLoading(true);
    try {
      const res = await axios.get(`${SERVER_BASE_URL}/booking/approval-preview/${ticketId}`, {
        headers: { Authorization: token },
      });
      if (res.status !== 200 || res.data.code !== 200 || !res.data.data) {
        toast.error(res.data.message || 'Could not load approval details');
        return;
      }
      const prev = res.data.data;
      if (prev.cannotApproveSoldOut) {
        toast.error(
          'This event is at capacity and waitlist is not enabled. You cannot approve this registration.'
        );
        return;
      }
      const showWaitlistCopy = prev.willWaitlist === true && prev.requireApproval === true;
      setTicketConfirmModal({
        open: true,
        type: 'approve',
        willWaitlist: showWaitlistCopy,
      });
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not load approval details');
    } finally {
      setApprovalPreviewLoading(false);
    }
  };

  const handleCloseTicketConfirm = () => {
    setTicketConfirmModal({ open: false, type: null, willWaitlist: false });
  };

  const handleConfirmTicketApprovalAction = async () => {
    const { type } = ticketConfirmModal;
    handleCloseTicketConfirm();
    if (!ticketId || !type) return;
    setTicketApprovalBusy(true);
    try {
      const url =
        type === 'approve'
          ? `${SERVER_BASE_URL}/booking/approve-ticket/${ticketId}`
          : `${SERVER_BASE_URL}/booking/reject-ticket/${ticketId}`;
      const res = await axios.post(url, {}, { headers: { Authorization: token } });
      if (res.data.code === 200) {
        if (type === 'approve') {
          const st = res.data.data?.status;
          toast.success(
            st === 'waitlisted'
              ? 'Registration approved and placed on the waitlist.'
              : 'Registration approved successfully'
          );
        } else {
          toast.success('Registration rejected');
        }
        await fetchDetails();
      } else {
        toast.error(res.data.message || 'Action failed');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Action failed');
    } finally {
      setTicketApprovalBusy(false);
    }
  };

  if (loading) {
    return <PageLoader fullPage />;
  }

  if (!orderDetails) {
    return (
      <div className={styles[bodyStyle]}>
        <div className={styles[dashboardStyle]} id={styles.dashboardcont2}>
          <div className="form_head">
            <div
              onClick={() => navigate(-1)}
              className={`${styles.arrowback} backBtn`}
              tabIndex={0}
              role="button"
            >
              Back
            </div>
          </div>
          <p style={{ padding: '40px', color: '#6B7280', textAlign: 'center' }}>
            Ticket not found.
          </p>
        </div>
      </div>
    );
  }

  const {
    orderId,
    organizerName: topLevelOrganizerName,
    transactionDetails = {},
    attendeeDetails = {},
    attendees = [],
    eventStatus = {},
    chargeBreakdown = {},
    activityLogs = [],
    foodAndBeverages = [],
    rafflePurchases = [],
    tierDetails = [],
    refundDetails,
  } = orderDetails;

  // ── Ticket-type helpers ──
  const evtType = eventStatus?.eventType;
  const isFormLikeEvt = evtType === 'form' || evtType === 'rsvp';
  const isTokenEvt = evtType === 'token';
  const isNonSeatEvt = evtType === 'non-seat-booking';
  const hasTierDetails = Array.isArray(tierDetails) && tierDetails.length > 0;
  const isSingleTier = hasTierDetails && tierDetails.length === 1;

  const ticketTypeLabel = (() => {
    if (isFormLikeEvt) return 'Registration';
    if (isTokenEvt) return 'Token Entry';
    if (isSingleTier) return tierDetails[0].label || 'Standard';
    if (hasTierDetails) return 'Multiple Tiers';
    return 'General Admission';
  })();

  // Returns null when no meaningful per-person price can be shown
  const pricePerAttendee = (() => {
    if (isFormLikeEvt || isTokenEvt) return null;
    if (isNonSeatEvt && hasTierDetails && !isSingleTier) return null;
    if (isNonSeatEvt && isSingleTier) return tierDetails[0].price || 0;
    if (attendeeDetails.numberOfTickets > 0) {
      return (chargeBreakdown.subtotal || 0) / attendeeDetails.numberOfTickets;
    }
    return 0;
  })();

  const formResponses = attendeeDetails?.formResponse?.responses || [];

  const isCancelled =
    transactionDetails?.status === 'cancelled' ||
    transactionDetails?.refundStatus === 'refunded' ||
    refundDetails != null;

  const isTicketPending = transactionDetails?.status === 'ticket-pending';
  const isWaitlisted = transactionDetails?.status === 'waitlisted';

  const statusBadge = isCancelled
    ? badgeCancelled
    : transactionDetails?.status === 'booked' || transactionDetails?.status === 'paid'
    ? badgePaid
    : isWaitlisted
    ? badgeWaitlisted
    : badgePending;

  const statusText = isCancelled
    ? 'Cancelled'
    : isTicketPending
    ? 'Pending'
    : isWaitlisted
    ? 'Waitlisted'
    : transactionDetails?.status === 'booked' || transactionDetails?.status === 'paid'
    ? 'Paid'
    : transactionDetails?.status || 'Pending';

  const organizerName = topLevelOrganizerName || null;

  return (
    <div className={styles[bodyStyle]}>
      <CancelTicket
        show={deleteModal}
        handleClose={() => setDeleteModal(false)}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteModal(false)}
        message="Do you really want to cancel this ticket?"
        loading={deleteLoading}
      />

      {ticketConfirmModal.open && (
        <div
          className={styles.vticketConfirmOverlay}
          role="dialog"
          aria-modal="true"
          aria-labelledby="vticketConfirmTitle"
          onClick={handleCloseTicketConfirm}
          onKeyDown={(e) => {
            if (e.key === 'Escape') handleCloseTicketConfirm();
          }}
        >
          <div className={styles.vticketConfirmCard} onClick={(e) => e.stopPropagation()}>
            <div
              className={`${styles.vticketConfirmIcon} ${
                ticketConfirmModal.type === 'approve'
                  ? styles.vticketConfirmIconApprove
                  : styles.vticketConfirmIconReject
              }`}
            >
              {ticketConfirmModal.type === 'approve' ? (
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 28 28"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M23 7L11 19L5 13"
                    stroke="#166534"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M4 4L20 20M20 4L4 20"
                    stroke="#991b1b"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  />
                </svg>
              )}
            </div>
            <h3 id="vticketConfirmTitle" className={styles.vticketConfirmTitle}>
              {ticketConfirmModal.type === 'approve'
                ? 'Approve Registration?'
                : 'Reject Registration?'}
            </h3>
            {ticketConfirmModal.type === 'approve' && ticketConfirmModal.willWaitlist ? (
              <p className={styles.vticketWaitlistNote}>
                <strong>Event is at capacity.</strong> Approving will place this registration on the{' '}
                <strong>waitlist</strong> (not a confirmed seat).
                <br />
                They will be notified if a spot opens.
              </p>
            ) : null}
            <p className={styles.vticketConfirmBody}>
              {ticketConfirmModal.type === 'approve' ? (
                <>
                  Are you sure you want to <strong>approve</strong> this registration
                  {attendeeDetails?.name ? (
                    <>
                      {' '}
                      for <strong>{attendeeDetails.name}</strong>
                    </>
                  ) : null}
                  ?
                  {ticketConfirmModal.willWaitlist
                    ? ' No ticket confirmation email will be sent while they remain on the waitlist.'
                    : ' A confirmation email with their ticket will be sent.'}
                </>
              ) : (
                <>
                  Are you sure you want to <strong>reject</strong> this registration
                  {attendeeDetails?.name ? (
                    <>
                      {' '}
                      for <strong>{attendeeDetails.name}</strong>
                    </>
                  ) : null}
                  ? A cancellation email will be sent and any payment will be refunded.
                </>
              )}
            </p>
            <div className={styles.vticketConfirmActions}>
              <button
                type="button"
                className={styles.vticketConfirmCancelBtn}
                onClick={handleCloseTicketConfirm}
              >
                Cancel
              </button>
              <button
                type="button"
                className={
                  ticketConfirmModal.type === 'approve'
                    ? styles.vticketConfirmApproveBtn
                    : styles.vticketConfirmRejectBtn
                }
                onClick={handleConfirmTicketApprovalAction}
                disabled={ticketApprovalBusy}
              >
                {ticketConfirmModal.type === 'approve' ? 'Yes, Approve' : 'Yes, Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={styles[dashboardStyle]} id={styles.dashboardcont2}>
        <div className={styles.pageWrapper}>
          {/* ── Page header ── */}
          <div className="form_head" style={{ marginBottom: '20px' }}>
            <div
              onClick={() => navigate(-1)}
              className={`${styles.arrowback} backBtn`}
              tabIndex={0}
              role="button"
            >
              <svg
                className="mr-1"
                width="17"
                height="14"
                viewBox="0 0 17 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15.5 6.00003H3.64L7.27 1.64003C7.43974 1.43581 7.5214 1.17253 7.49702 0.908108C7.47264 0.643682 7.34422 0.39977 7.14 0.230032C6.93578 0.060293 6.6725 -0.0213689 6.40808 0.0030108C6.14365 0.0273905 5.89974 0.155815 5.73 0.360032L0.73 6.36003C0.696361 6.40776 0.666279 6.45789 0.64 6.51003C0.64 6.56003 0.64 6.59003 0.57 6.64003C0.524674 6.75469 0.500941 6.87674 0.5 7.00003C0.500941 7.12332 0.524674 7.24537 0.57 7.36003C0.57 7.41003 0.57 7.44003 0.64 7.49003C0.666279 7.54217 0.696361 7.59231 0.73 7.64003L5.73 13.64C5.82402 13.7529 5.94176 13.8437 6.07485 13.9059C6.20793 13.9681 6.35309 14.0003 6.5 14C6.73365 14.0005 6.96009 13.9191 7.14 13.77C7.24126 13.6861 7.32496 13.583 7.38631 13.4666C7.44766 13.3503 7.48546 13.223 7.49754 13.092C7.50961 12.961 7.49573 12.829 7.45669 12.7034C7.41764 12.5777 7.3542 12.4611 7.27 12.36L3.64 8.00003H15.5C15.7652 8.00003 16.0196 7.89467 16.2071 7.70714C16.3946 7.5196 16.5 7.26525 16.5 7.00003C16.5 6.73482 16.3946 6.48046 16.2071 6.29292C16.0196 6.10539 15.7652 6.00003 15.5 6.00003Z"
                  fill="currentColor"
                />
              </svg>
              Back
            </div>
          </div>

          <div className={styles.pageLayout}>
            {/* ── Main column ── */}
            <div className={styles.mainPanel}>
              {/* Status + total */}
              <div className={styles.statusCard}>
                <div className={styles.statusLeft}>
                  <span style={statusBadge}>{statusText}</span>
                  <span style={{ fontSize: '12px', color: '#9CA3AF' }}>
                    Order placed {getTimeAgo(transactionDetails?.createdAt)}
                  </span>
                </div>
                <div className={styles.statusRight}>
                  <div className={styles.priceAmount}>
                    {fmt(chargeBreakdown?.totalAmount || transactionDetails?.amount)}
                  </div>
                  <div style={{ fontSize: '12px', color: '#9CA3AF' }}>Total paid</div>
                </div>
              </div>

              {/* Order Information */}
              <div style={card}>
                <h2 style={cardTitle}>Order Information</h2>
                <div className={styles.infoGrid}>
                  <div>
                    <div style={infoRow}>
                      <span style={label}>Order Date</span>
                      <span style={value}>{fmtDateTime(transactionDetails?.createdAt)}</span>
                    </div>
                    <div style={infoRow}>
                      <span style={label}>Payment Method</span>
                      <span style={value}>{transactionDetails?.paymentMethod || '—'}</span>
                    </div>
                    <div style={infoRow}>
                      <span style={label}>Transaction ID</span>
                      <span style={{ ...value, wordBreak: 'break-all', fontSize: '13px' }}>
                        {transactionDetails?.transactionId ||
                          transactionDetails?.paymentIntentId ||
                          '—'}
                      </span>
                    </div>
                    <div style={infoRow}>
                      <span style={label}>Booking ID</span>
                      <span style={value}>{orderId || '—'}</span>
                    </div>
                  </div>
                  <div>
                    <div style={infoRow}>
                      <span style={label}>Event</span>
                      <span style={{ ...value, color: '#39979d' }}>
                        {eventStatus?.eventName || '—'}
                      </span>
                    </div>
                    <div style={infoRow}>
                      <span style={label}>Organizer</span>
                      <span style={value}>{organizerName || '—'}</span>
                    </div>
                    <div style={infoRow}>
                      <span style={label}>Discount Applied</span>
                      <span
                        style={{
                          ...value,
                          color: chargeBreakdown?.discountAmount > 0 ? '#16A34A' : '#111827',
                        }}
                      >
                        {chargeBreakdown?.discountAmount > 0
                          ? `-${fmt(chargeBreakdown.discountAmount)} ${
                              chargeBreakdown?.couponCode ? `(${chargeBreakdown.couponCode})` : ''
                            }`
                          : '—'}
                      </span>
                    </div>
                    {attendeeDetails?.seatsBooked && (
                      <div style={infoRow}>
                        <span style={label}>Seats Booked</span>
                        <span style={{ ...value, wordBreak: 'break-all' }}>
                          {attendeeDetails.seatsBooked}
                        </span>
                      </div>
                    )}
                    <div style={infoRow}>
                      <span style={label}>Customer Email</span>
                      <span style={{ ...value, fontSize: '13px' }}>
                        {attendeeDetails?.email || '—'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Form Responses */}
              {formResponses.length > 0 && (
                <div style={card}>
                  <h2 style={cardTitle}>Form Responses</h2>
                  {formResponses.map((item) => (
                    <div key={item._id || item.label} style={formResponseItem}>
                      <span style={{ ...label, color: '#6B7280', fontWeight: '600' }}>
                        {item.label}
                      </span>
                      {isFileUrl(item.response) ? (
                        <a
                          href={item.response}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: '#39979d',
                            fontSize: '14px',
                            textDecoration: 'underline',
                          }}
                        >
                          {getFileName(item.response)}
                        </a>
                      ) : (
                        <span
                          style={{ ...value, color: item.response?.trim() ? '#111827' : '#9CA3AF' }}
                        >
                          {item.response?.trim() || 'Not provided'}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Attendees & Tickets */}
              <div style={card}>
                <h2 style={cardTitle}>Attendees &amp; Tickets</h2>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                    <thead>
                      <tr style={{ background: '#F9FAFB' }}>
                        {['Attendee', 'Ticket Type', 'Seat', 'Price'].map((h) => (
                          <th
                            key={h}
                            style={{
                              padding: '10px 14px',
                              textAlign: 'left',
                              fontWeight: '600',
                              fontSize: '12px',
                              color: '#6B7280',
                              borderBottom: '1px solid #E5E7EB',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {attendees.length > 0 ? (
                        attendees.map((attendee, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid #F3F4F6' }}>
                            <td style={{ padding: '12px 14px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span
                                  style={{
                                    width: '34px',
                                    height: '34px',
                                    borderRadius: '50%',
                                    background: '#EDE9FE',
                                    color: '#7C3AED',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: '700',
                                    fontSize: '13px',
                                    flexShrink: 0,
                                  }}
                                >
                                  {getInitials(attendee.name)}
                                </span>
                                <div>
                                  <div style={{ fontWeight: '600', color: '#111827' }}>
                                    {attendee.name}
                                    {attendeeDetails?.bookingForSomeone ? (
                                      <span
                                        style={{
                                          marginLeft: '6px',
                                          fontSize: '11px',
                                          color: '#9CA3AF',
                                          fontWeight: '400',
                                        }}
                                      >
                                        {attendee.isBooker ? '(Booker)' : '(Attendee)'}
                                      </span>
                                    ) : (
                                      attendee.isBooker && (
                                        <span
                                          style={{
                                            marginLeft: '6px',
                                            fontSize: '11px',
                                            color: '#9CA3AF',
                                            fontWeight: '400',
                                          }}
                                        >
                                          (Booker)
                                        </span>
                                      )
                                    )}
                                  </div>
                                  <div style={{ fontSize: '12px', color: '#9CA3AF' }}>
                                    {attendee.email || '—'}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td style={{ padding: '12px 14px', color: '#374151' }}>
                              {ticketTypeLabel}
                            </td>
                            <td style={{ padding: '12px 14px', color: '#374151' }}>
                              {attendee.seats?.length ? attendee.seats.join(', ') : '—'}
                            </td>
                            <td style={{ padding: '12px 14px', color: '#374151' }}>
                              {pricePerAttendee !== null ? fmt(pricePerAttendee) : '—'}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan="4"
                            style={{ padding: '40px', textAlign: 'center', color: '#9CA3AF' }}
                          >
                            No attendees found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Ticket Breakdown — shown for non-seat / form events that have tier data */}
              {hasTierDetails && (
                <div style={card}>
                  <h2 style={cardTitle}>Ticket Breakdown</h2>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                      <thead>
                        <tr style={{ background: '#F9FAFB' }}>
                          {['Ticket Type', 'Quantity', 'Unit Price', 'Line Total'].map((h) => (
                            <th
                              key={h}
                              style={{
                                padding: '10px 14px',
                                textAlign: 'left',
                                fontWeight: '600',
                                fontSize: '12px',
                                color: '#6B7280',
                                borderBottom: '1px solid #E5E7EB',
                              }}
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {tierDetails.map((tier, idx) => {
                          const qty = tier.quantity || 0;
                          const unitPrice = tier.price || 0;
                          const lineTotal = qty * unitPrice;
                          return (
                            <tr key={idx} style={{ borderBottom: '1px solid #F3F4F6' }}>
                              <td style={{ padding: '10px 14px' }}>{tier.label || 'Standard'}</td>
                              <td style={{ padding: '10px 14px' }}>{qty}</td>
                              <td style={{ padding: '10px 14px' }}>{fmt(unitPrice)}</td>
                              <td style={{ padding: '10px 14px' }}>{fmt(lineTotal)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div
                    style={{
                      marginTop: '10px',
                      fontSize: '13px',
                      color: '#6B7280',
                      textAlign: 'right',
                    }}
                  >
                    Tickets Subtotal:{' '}
                    <strong style={{ color: '#111827' }}>
                      {fmt(tierDetails.reduce((s, t) => s + (t.price || 0) * (t.quantity || 0), 0))}
                    </strong>
                  </div>
                </div>
              )}

              {/* Food & Beverage */}
              {foodAndBeverages && foodAndBeverages.length > 0 && (
                <div style={card}>
                  <h2 style={cardTitle}>Food &amp; Beverage Preorders</h2>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                      <thead>
                        <tr style={{ background: '#F9FAFB' }}>
                          {['Item', 'Qty', 'Unit Price', 'Total'].map((h) => (
                            <th
                              key={h}
                              style={{
                                padding: '10px 14px',
                                textAlign: 'left',
                                fontWeight: '600',
                                fontSize: '12px',
                                color: '#6B7280',
                                borderBottom: '1px solid #E5E7EB',
                              }}
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {foodAndBeverages.map((item, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid #F3F4F6' }}>
                            <td style={{ padding: '10px 14px' }}>
                              {item.name || item.itemName || '—'}
                            </td>
                            <td style={{ padding: '10px 14px' }}>{item.quantity || 0}</td>
                            <td style={{ padding: '10px 14px' }}>
                              {fmt(item.price || item.pricePerItem || 0)}
                            </td>
                            <td style={{ padding: '10px 14px' }}>
                              {fmt((item.price || item.pricePerItem || 0) * (item.quantity || 0))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Raffle Ticket Purchases */}
              {rafflePurchases && rafflePurchases.length > 0 && (
                <div style={card}>
                  <h2 style={cardTitle}>Raffle Ticket Purchases</h2>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                      <thead>
                        <tr style={{ background: '#F9FAFB' }}>
                          {['Raffle Name', 'Quantity', 'Price per Ticket', 'Line Total'].map(
                            (h) => (
                              <th
                                key={h}
                                style={{
                                  padding: '10px 14px',
                                  textAlign: 'left',
                                  fontWeight: '600',
                                  fontSize: '12px',
                                  color: '#6B7280',
                                  borderBottom: '1px solid #E5E7EB',
                                }}
                              >
                                {h}
                              </th>
                            )
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {rafflePurchases.map((item, idx) => {
                          const pricePerTicket = Number(item.pricePerTicket) || 0;
                          const qty = Number(item.quantity) || 0;
                          const lineTotal =
                            item.price != null ? Number(item.price) : qty * pricePerTicket;
                          return (
                            <tr key={idx} style={{ borderBottom: '1px solid #F3F4F6' }}>
                              <td style={{ padding: '10px 14px' }}>
                                {item.name || item.raffleName || '—'}
                              </td>
                              <td style={{ padding: '10px 14px' }}>{qty}</td>
                              <td style={{ padding: '10px 14px' }}>{fmt(pricePerTicket)}</td>
                              <td style={{ padding: '10px 14px' }}>{fmt(lineTotal)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Charges Breakdown */}
              <div style={card}>
                <h2 style={cardTitle}>Charges Breakdown</h2>
                <div style={breakdownRow}>
                  <span style={{ color: '#6B7280', fontSize: '14px' }}>Tickets Subtotal</span>
                  <span style={{ fontWeight: '500', fontSize: '14px' }}>
                    {fmt(chargeBreakdown?.subtotal || 0)}
                  </span>
                </div>
                {(chargeBreakdown?.convenienceFee || 0) + (chargeBreakdown?.donationConvFee || 0) >
                  0 && (
                  <div style={breakdownRow}>
                    <span style={{ color: '#6B7280', fontSize: '14px' }}>Convenience Fee</span>
                    <span style={{ fontWeight: '500', fontSize: '14px' }}>
                      {fmt(
                        (chargeBreakdown.convenienceFee || 0) +
                          (chargeBreakdown.donationConvFee || 0)
                      )}
                    </span>
                  </div>
                )}
                {chargeBreakdown?.discountAmount > 0 && (
                  <div style={breakdownRow}>
                    <span style={{ color: '#16A34A', fontSize: '14px' }}>Discount</span>
                    <span style={{ color: '#16A34A', fontWeight: '500', fontSize: '14px' }}>
                      –{fmt(chargeBreakdown.discountAmount)}
                    </span>
                  </div>
                )}
                {chargeBreakdown?.discountAmount > 0 && chargeBreakdown?.couponCode && (
                  <div style={breakdownRow}>
                    <span style={{ color: '#6B7280', fontSize: '14px' }}>Coupon Applied</span>
                    <span style={{ fontWeight: '500', fontSize: '14px', color: '#111827' }}>
                      {chargeBreakdown.couponCode}
                    </span>
                  </div>
                )}
                {chargeBreakdown?.donationAmount > 0 && (
                  <div style={breakdownRow}>
                    <span style={{ color: '#6B7280', fontSize: '14px' }}>Donation Amount</span>
                    <span style={{ fontWeight: '500', fontSize: '14px' }}>
                      {fmt(chargeBreakdown.donationAmount)}
                    </span>
                  </div>
                )}
                <div style={breakdownTotal}>
                  <span>Total</span>
                  <span>{fmt(chargeBreakdown?.totalAmount || 0)}</span>
                </div>
              </div>

              {/* Payment Details */}
              <div style={card}>
                <h2 style={cardTitle}>Payment Details</h2>
                <div className={styles.infoGrid}>
                  <div>
                    <div style={infoRow}>
                      <span style={label}>Payment Method</span>
                      <span style={value}>{transactionDetails?.paymentMethod || '—'}</span>
                    </div>
                    <div style={infoRow}>
                      <span style={label}>Authorization</span>
                      <span
                        style={{
                          ...value,
                          color:
                            transactionDetails?.status === 'booked' ||
                            transactionDetails?.status === 'paid'
                              ? '#16A34A'
                              : '#374151',
                        }}
                      >
                        {transactionDetails?.status === 'booked' ||
                        transactionDetails?.status === 'paid'
                          ? 'Captured'
                          : transactionDetails?.status === 'waitlisted'
                          ? 'Waitlisted'
                          : transactionDetails?.status === 'ticket-pending'
                          ? 'Pending approval'
                          : transactionDetails?.status || '—'}
                      </span>
                    </div>
                    <div style={infoRow}>
                      <span style={label}>Settlement Date</span>
                      <span style={value}>{fmtDate(transactionDetails?.updatedAt)}</span>
                    </div>
                  </div>
                  <div>
                    <div style={infoRow}>
                      <span style={label}>Refunded Amount</span>
                      <span style={value}>
                        {fmt(
                          isCancelled
                            ? refundDetails?.refundAmount ?? chargeBreakdown?.totalAmount ?? 0
                            : 0
                        )}
                      </span>
                    </div>
                    <div style={infoRow}>
                      <span style={label}>Status</span>
                      <span
                        style={{
                          ...value,
                          color: isCancelled
                            ? '#DC2626'
                            : isWaitlisted
                            ? '#5B21B6'
                            : isTicketPending
                            ? '#854D0E'
                            : '#16A34A',
                        }}
                      >
                        {isCancelled
                          ? 'Refunded / Cancelled'
                          : isWaitlisted
                          ? 'Waitlisted'
                          : isTicketPending
                          ? 'Pending approval'
                          : 'Active'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Activity Log */}
              {activityLogs && activityLogs.length > 0 && (
                <div style={card}>
                  <h2 style={cardTitle}>Activity Log</h2>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                    {activityLogs.map((log, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: 'flex',
                          gap: '12px',
                          paddingBottom: '16px',
                          marginBottom: '4px',
                        }}
                      >
                        <div
                          style={{
                            width: '10px',
                            height: '10px',
                            borderRadius: '50%',
                            background:
                              log.type === 'payment'
                                ? '#22C55E'
                                : log.type === 'refund'
                                ? '#F97316'
                                : '#3B82F6',
                            marginTop: '4px',
                            flexShrink: 0,
                          }}
                        />
                        <div>
                          <div style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                            {log.action}
                          </div>
                          {log.description && (
                            <div style={{ fontSize: '12px', color: '#6B7280' }}>
                              {log.description}
                            </div>
                          )}
                          <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '2px' }}>
                            {fmtDateTime(log.timestamp)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ── Sidebar ── */}
            <div className={styles.sidebarPanel}>
              <div style={card}>
                <h3 style={{ ...cardTitle, marginBottom: '14px' }}>Quick Actions</h3>
                {!isCancelled && isTicketPending && (
                  <>
                    <button
                      type="button"
                      className={styles.quickActionApprove}
                      disabled={ticketApprovalBusy || approvalPreviewLoading}
                      onClick={handlePrepareApproveConfirm}
                    >
                      {approvalPreviewLoading ? (
                        <span style={{ fontSize: '13px' }}>Please wait…</span>
                      ) : (
                        <>
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 14 14"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                          >
                            <path
                              d="M12.25 3.5L5.25 10.5L1.75 7"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          Approve registration
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      className={styles.quickActionReject}
                      disabled={ticketApprovalBusy || approvalPreviewLoading}
                      onClick={handleOpenTicketConfirmReject}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path
                          d="M1 1L11 11M11 1L1 11"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                      Reject registration
                    </button>
                  </>
                )}
                {!isCancelled && !isTicketPending && (
                  <button type="button" style={btnDanger} onClick={() => setDeleteModal(true)}>
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="8" y1="8" x2="16" y2="16" />
                      <line x1="16" y1="8" x2="8" y2="16" />
                    </svg>
                    Cancel Ticket
                  </button>
                )}
                {isCancelled && (
                  <div
                    style={{
                      background: '#FEE2E2',
                      borderRadius: '8px',
                      padding: '12px',
                      color: '#991B1B',
                      fontSize: '13px',
                      textAlign: 'center',
                    }}
                  >
                    This ticket has been cancelled
                  </div>
                )}
              </div>

              <div style={card}>
                <h3 style={{ ...cardTitle, marginBottom: '14px' }}>Ticket Summary</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '13px', color: '#6B7280' }}>Event</span>
                    <span
                      style={{
                        fontSize: '13px',
                        fontWeight: '500',
                        color: '#111827',
                        textAlign: 'right',
                        maxWidth: '160px',
                      }}
                    >
                      {eventStatus?.eventName || '—'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '13px', color: '#6B7280' }}>Attendee</span>
                    <span style={{ fontSize: '13px', fontWeight: '500', color: '#111827' }}>
                      {attendeeDetails?.name || '—'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '13px', color: '#6B7280' }}>Tickets</span>
                    <span style={{ fontSize: '13px', fontWeight: '500', color: '#111827' }}>
                      {attendeeDetails?.numberOfTickets || 1}
                    </span>
                  </div>
                  {attendeeDetails?.seatsBooked && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                      <span style={{ fontSize: '13px', color: '#6B7280', flexShrink: 0 }}>
                        Seats
                      </span>
                      <span
                        style={{
                          fontSize: '13px',
                          fontWeight: '500',
                          color: '#111827',
                          textAlign: 'right',
                          wordBreak: 'break-all',
                        }}
                      >
                        {attendeeDetails.seatsBooked}
                      </span>
                    </div>
                  )}
                  {chargeBreakdown?.donationAmount > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '13px', color: '#6B7280' }}>Donation</span>
                      <span style={{ fontSize: '13px', fontWeight: '500', color: '#111827' }}>
                        {fmt(chargeBreakdown.donationAmount)}
                      </span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '13px', color: '#6B7280' }}>Event Date</span>
                    <span style={{ fontSize: '13px', fontWeight: '500', color: '#111827' }}>
                      {fmtDate(eventStatus?.startDate)}
                    </span>
                  </div>
                  <div
                    style={{
                      borderTop: '1px solid #E5E7EB',
                      paddingTop: '10px',
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>
                      Total
                    </span>
                    <span style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>
                      {fmt(chargeBreakdown?.totalAmount || 0)}
                    </span>
                  </div>
                </div>
              </div>

              <div style={card}>
                <h3 style={{ ...cardTitle, marginBottom: '14px' }}>Event Progress</h3>
                <div
                  style={{
                    fontSize: '13px',
                    color: '#6B7280',
                    marginBottom: '6px',
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <span>Check-in</span>
                  <span>
                    {eventStatus?.stats?.checkedInTickets || 0}/
                    {eventStatus?.stats?.totalTickets || 0}
                  </span>
                </div>
                <div
                  style={{
                    height: '6px',
                    borderRadius: '4px',
                    background: '#E5E7EB',
                    overflow: 'hidden',
                    marginBottom: '14px',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      background: '#39979d',
                      borderRadius: '4px',
                      width:
                        eventStatus?.stats?.totalTickets > 0
                          ? `${Math.min(
                              100,
                              Math.round(
                                (eventStatus.stats.checkedInTickets /
                                  eventStatus.stats.totalTickets) *
                                  100
                              )
                            )}%`
                          : '0%',
                    }}
                  />
                </div>
                <div
                  style={{
                    fontSize: '13px',
                    color: '#6B7280',
                    marginBottom: '6px',
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <span>Capacity</span>
                  <span>
                    {eventStatus?.stats?.totalTickets || 0}/{eventStatus?.stats?.totalCapacity || 0}
                  </span>
                </div>
                <div
                  style={{
                    height: '6px',
                    borderRadius: '4px',
                    background: '#E5E7EB',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      background: '#22C55E',
                      borderRadius: '4px',
                      width:
                        eventStatus?.stats?.totalCapacity > 0
                          ? `${Math.min(
                              100,
                              Math.round(
                                (eventStatus.stats.totalTickets / eventStatus.stats.totalCapacity) *
                                  100
                              )
                            )}%`
                          : '0%',
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ViewTicket;
